import { useState, useEffect } from "react";
import { Modal } from "./Modal";
import { Btn } from "./Btn";
import { MonthPicker } from "./MonthPicker";
import { toast } from "./Toast";
import { salaryHistoryApi, monthLabelToYM } from "../../lib/salaryHistoryApi";

/* Revise an employee's pay, effective from a chosen month.
 *
 * Employee.baseSalary is one figure with no time dimension, so payroll used
 * to read the CURRENT salary for every month -- a rise silently recomputed
 * every closed month. salary_history holds a row per effective month and
 * payroll picks the latest one at or before the month it is computing.
 *
 * The picker offers the current month onward only, matching the API's own
 * rejection: a past month is closed, and letting one be chosen here would
 * reopen exactly what this exists to protect. The current month IS allowed,
 * so a mistyped figure is correctable within the month it was entered.
 *
 * Existing rows are listed so a rise already scheduled is visible -- without
 * that, an admin could schedule a second one for the same month and simply
 * overwrite the first without realising. */

const MONTHS = ["January","February","March","April","May","June",
                "July","August","September","October","November","December"];

const ymToLabel = (ym) => {
  const [y, m] = String(ym || "").split("-");
  const i = Number(m) - 1;
  return i >= 0 && i < 12 ? `${MONTHS[i]} ${y}` : ym;
};

const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN");

export function SalaryRevisionModal({ employee, onClose, onSaved }) {
  const now = new Date();
  const currentYM = now.toISOString().slice(0, 7);

  const [rows, setRows] = useState(null);
  const [amount, setAmount] = useState("");
  const [monthLabel, setMonthLabel] = useState(
    `${MONTHS[now.getMonth()]} ${now.getFullYear()}`,
  );
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    salaryHistoryApi.listAll()
      .then((all) => { if (alive) setRows(all.filter((r) => r.employeeId === employee.id)); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [employee.id]);

  const chosenYM = monthLabelToYM(monthLabel);
  const isPast = chosenYM != null && chosenYM < currentYM;
  const amountNum = Number(amount);
  const valid = amount !== "" && amountNum >= 0 && !isPast;

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await salaryHistoryApi.upsert(employee.id, chosenYM, amountNum, note);
      toast(`Salary set to ${inr(amountNum)} from ${monthLabel}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast(e?.message || "Could not save the revision");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Revise salary · ${employee.name}`} onClose={onClose} size="sm" dismissOnBackdrop={false}>
      <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">
            New monthly salary <span className="text-red-600">*</span>
          </label>
          <input
            type="number" min="0" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 30000"
            className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Effective from</label>
          <MonthPicker value={monthLabel} onChange={setMonthLabel} align="left" />
          {isPast ? (
            <p className="text-[11px] text-red-600 mt-1.5">
              {monthLabel} is closed. Payroll for a past month cannot change — pick this month or later.
            </p>
          ) : (
            <p className="text-[11px] text-slate-500 mt-1.5">
              Payroll from {monthLabel} onward uses this rate. Earlier months keep theirs.
            </p>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">
            Note <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Annual increment"
            className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="pt-1">
          <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-2">
            Salary history
          </div>
          {rows === null ? (
            <p className="text-xs text-slate-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-xs text-slate-400">No revisions recorded yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {rows.slice().sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom)).map((r) => (
                <li key={r.effectiveFrom} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-slate-500">
                    {ymToLabel(r.effectiveFrom)}
                    {r.effectiveFrom > currentYM && (
                      <span className="ml-1.5 text-[10px] font-semibold text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
                        scheduled
                      </span>
                    )}
                  </span>
                  <span className="font-semibold text-slate-900 tabular-nums">{inr(r.baseSalary)}</span>
                </li>
              ))}
            </ul>
          )}
          {rows?.some((r) => r.effectiveFrom === chosenYM) && (
            <p className="text-[11px] text-amber-700 mt-2">
              A rate already exists for {monthLabel} — saving replaces it.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={!valid || saving}>
            {saving ? "Saving…" : "Save revision"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
